import {
  CalendarEvent,
  google,
  office365,
  outlook,
  yahoo,
} from 'calendar-link';
import type { CalendarEventOrganizer } from 'calendar-link/dist/interfaces';
import { add, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { converter } from '@libs/src/email-wysiwyg/email-wysiwyg.helper';

export function buildCalendarLink(calendarEvent: CalendarEvent) {
  const eventWithHTMLDescription = {
    ...calendarEvent,
    description: converter.makeHtml(calendarEvent.description),
  };
  return {
    google: google(eventWithHTMLDescription),
    ics: createIcsEvent({
      ...eventWithHTMLDescription,
      originalDescription: calendarEvent.description,
    }),
    office: _preserveSpaceInOffice365(eventWithHTMLDescription),
    office365: _preserveSpaceInOffice365(eventWithHTMLDescription).replace(
      'https://outlook.office.com/',
      'https://outlook.office365.com/',
    ),
    outlook: _preserveSpaceInOutlook(eventWithHTMLDescription),
    yahoo: yahoo(eventWithHTMLDescription),
  };
}

function _preserveSpaceInOffice365(calendarEvent: CalendarEvent) {
  return office365(calendarEvent).replace('&rru=addevent', '');
}

function _preserveSpaceInOutlook(calendarEvent: CalendarEvent) {
  return outlook(calendarEvent).replace('&rru=addevent', '');
}

export function downloadIcs(icsString: string, fileName: string) {
  const calendarDetails = decodeURIComponent(icsString.split(',')[1]);
  const mimeType = icsString.split(',')[0];
  const icsFile = new Blob([calendarDetails], { type: mimeType });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(icsFile);
  link.download = `${fileName}.ics`;
  link.click();
}

export const calendarProviderConfigs = [
  {
    logoUrl:
      '//storage.googleapis.com/ttc-static-images/calendar/google-calendar.5C2319E.png',
    name: 'google',
    displayName: 'Google',
  },
  {
    logoUrl:
      '//storage.googleapis.com/ttc-static-images/calendar/office.2D11EE4.svg',
    name: 'office',
    displayName: 'Office',
  },
  {
    logoUrl:
      '//storage.googleapis.com/ttc-static-images/calendar/microsoft.C711088.svg',
    name: 'office365',
    displayName: 'Office365',
  },
  {
    logoUrl:
      '//storage.googleapis.com/ttc-static-images/calendar/outlook.4D889B7.png',
    name: 'outlook',
    displayName: 'Outlook',
  },
  {
    logoUrl: '//storage.googleapis.com/ttc-static-images/yahoo-logo.png',
    name: 'yahoo',
    displayName: 'Yahoo',
  },
  {
    logoUrl:
      '//storage.googleapis.com/ttc-static-images/emoji-spiral-calendar-pad.7ABA31D7.png',
    name: 'ics',
    displayName: 'iCalendar',
  },
];

function createIcsEvent(
  calendarEvent: CalendarEvent & { originalDescription: string },
): string {
  const dtStamp = getISOFormatWithoutOffset(new Date());
  let end: string;
  if (calendarEvent.end) {
    end = getISOFormatWithoutOffset(new Date(calendarEvent.end));
  } else {
    const calculatedEnd = getEndFromDuration(
      new Date(calendarEvent.start),
      calendarEvent.duration,
    );
    end = getISOFormatWithoutOffset(calculatedEnd);
  }
  const start = getISOFormatWithoutOffset(new Date(calendarEvent.start));
  const formattedHTMLDescription = formatLineEndings(calendarEvent.description);
  const formattedLocation = formatLineEndings(calendarEvent.location);
  const formattedOriginalDescription = formatLineEndings(
    calendarEvent.originalDescription,
  );
  const calendarChunks = [
    {
      key: 'BEGIN',
      value: 'VCALENDAR',
    },
    {
      key: 'VERSION',
      value: '2.0',
    },
    {
      key: 'PRODID',
      value: calendarEvent.title,
    },
    {
      key: 'BEGIN',
      value: 'VEVENT',
    },
    {
      key: 'URL',
      value: calendarEvent.url,
    },
    {
      key: 'DTSTART',
      value: start,
    },
    {
      key: 'DTEND',
      value: end,
    },
    {
      key: 'DTSTAMP',
      value: dtStamp,
    },
    {
      key: 'RRULE',
      value: calendarEvent.rRule,
    },
    {
      key: 'SUMMARY',
      value: calendarEvent.title,
    },
    {
      key: 'DESCRIPTION',
      value: formattedOriginalDescription,
    },
    {
      key: 'X-ALT-DESC;FMTTYPE=text/html',
      value: `<html>${formattedHTMLDescription}</html>`,
    },
    {
      key: 'LOCATION',
      value: formattedLocation,
    },
    {
      key: 'ORGANIZER',
      value: calendarEvent.organizer,
    },
    {
      key: 'UID',
      value: Math.floor(Math.random() * 100000)
        .toString()
        .replace('.', ''),
    },
    {
      key: 'END',
      value: 'VEVENT',
    },
    {
      key: 'END',
      value: 'VCALENDAR',
    },
  ];
  let calendarUrl = '';
  calendarChunks.forEach((chunk) => {
    if (chunk.value) {
      if ('ORGANIZER' === chunk.key) {
        const value = chunk.value as CalendarEventOrganizer;
        calendarUrl += `${chunk.key};${encodeURIComponent(`CN=${value.name}:MAILTO:${value.email}\r\n`)}`;
      } else {
        calendarUrl += `${chunk.key}:${encodeURIComponent(`${chunk.value}\r\n`)}`;
      }
    }
  });
  return `data:text/calendar;charset=utf8,${calendarUrl}`;
}

function getISOFormatWithoutOffset(date: Date) {
  const utcDate = utcToZonedTime(date, 'UTC');
  return format(date, 'yMMdd') + 'T' + format(utcDate, 'HHmmSS') + 'Z';
}

function getEndFromDuration(
  startDate: Date,
  [periodDuration, periodUnit]: CalendarEvent['duration'] = [60, 'minutes'],
): Date {
  return add(startDate, { [periodUnit]: periodDuration });
}

function formatLineEndings(content: string): string {
  return (content || '')
    .replace(/,/gm, ',')
    .replace(/;/gm, ';')
    .replace(/\r\n/gm, '\n')
    .replace(/\n/gm, '\\n')
    .replace(/(\\n)[\s\t]+/gm, '\\n');
}
